/**
 * Shared utility functions for formatting filter values across different contexts.
 * Used by both query parameter parsing and analytics tracking.
 */

/**
 * Converts a number to a string for serialization.
 * @param value - The number value to stringify
 */
export const stringifyNumber = (value?: number | null): string | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value !== "number" || isNaN(value)) return undefined;
  return String(value);
};

/**
 * Converts a boolean to a string for serialization.
 */
export const stringifyBoolean = (
  value?: boolean | null
): string | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value !== "boolean") return undefined;
  return String(value);
};

/**
 * Parses a value (string, number, or null) into a number.
 * Explicitly handles null and undefined to prevent Number(null) = 0.
 */
export const parseNumber = (
  value?: string | number | null
): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  if (typeof value === "number" && !isNaN(value)) return value;
  const parsed = Number(value);
  return !isNaN(parsed) ? parsed : undefined;
};

/**
 * Parses a value (string, boolean, or null) into a boolean.
 */
export const parseBoolean = (
  value?: string | boolean | null
): boolean | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
};
